<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { LoginMethod } from '@/types'

const props = withDefaults(
  defineProps<{
    /** Button with cool-down bar, or quiet inline text link (magic link note). */
    variant?: 'button' | 'link'
  }>(),
  {
    variant: 'button',
  }
)

const store = useStore()

const TIMEOUT = 30000
let buttonTimer: ReturnType<typeof setTimeout> | undefined

const isSending = ref(false)
const progress = ref(0)
const timeout = ref(TIMEOUT)

const loginMethod = computed(() => store.state.auth.loginMethod)
const authEmail = computed(() => store.state.auth.authEmail)

const resendButtonText = computed(() => {
  return loginMethod.value === LoginMethod.CODE
    ? 'Buttons.ResendCode'
    : 'Buttons.ResendLink'
})

const progressBarStyle = computed(() => ({
  width: `${progress.value}%`,
  transition: `width ${timeout.value}ms linear`,
}))

const resendLink = async () => {
  // Local cool-down plus shared btnLoading from sendLoginEmail.
  if (isSending.value || store.state.auth.btnLoading) return

  isSending.value = true
  progress.value = 0
  timeout.value = TIMEOUT

  requestAnimationFrame(() => {
    progress.value = 100
  })

  try {
    await store.dispatch('sendLoginEmail', {
      email: authEmail.value,
      send: loginMethod.value,
    })
  } finally {
    // Cool-down to prevent resend spam (keeps control disabled after send settles).
    buttonTimer = setTimeout(() => {
      isSending.value = false
      progress.value = 0
      timeout.value = 0
    }, TIMEOUT)
  }
}

onUnmounted(() => {
  clearTimeout(buttonTimer)
})
</script>

<template>
  <button
    v-if="variant === 'link'"
    type="button"
    class="sa-auth-resend-link"
    :disabled="isSending"
    data-testid="resend-link-button"
    @click="resendLink"
  >
    {{ isSending ? $t('Buttons.Resending') : $t(resendButtonText) }}
  </button>
  <button
    v-else
    type="button"
    class="sa-btn-secondary sa-auth-resend"
    :class="{ 'is-cooling': isSending }"
    :disabled="isSending"
    data-testid="resend-link-button"
    @click="resendLink"
  >
    <span>{{
      isSending ? $t('Buttons.Resending') : $t(resendButtonText)
    }}</span>
    <span class="sa-auth-resend__bar" :style="progressBarStyle" />
  </button>
</template>
